import { logoSrc, tamponSrc } from '@/crm/contract/branding';
import { RIB_IMAGE_DATA_URI } from '@/crm/contract/annexesDataUri';

import {
  ARTICLE_3BIS,
  ARTICLE_3BIS_INTRO,
  ARTICLES,
  ContratData,
  DEPASSEMENTS_TEXTE,
  DROIT_IMAGE_CLIENT,
  PREAMBULE_CGE,
  PRESTATAIRES,
  RIB_CYGNATURE,
  Structure,
  VALIDITE_RESERVATION,
} from './content';

function escHtml(s: string): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

// Convertit le **gras** markdown en <strong> et préserve les sauts de ligne.
function inline(s: string, prestataireNom: string, retourAdresse: string, retourEmail: string): string {
  const replaced = String(s ?? '')
    .split('__PRESTATAIRE__').join(prestataireNom)
    .split('__RETOUR_ADRESSE__').join(retourAdresse)
    .split('__RETOUR_EMAIL__').join(retourEmail);
  return escHtml(replaced).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

const cb = (on?: boolean) => `<span class="cb">${on ? '☒' : '☐'}</span>`;
const v = (s?: string, fallback = '—') => (s && String(s).trim() ? escHtml(String(s)) : fallback);

export function renderContratHTML(structure: Structure, data: ContratData): string {
  const p = PRESTATAIRES[structure];
  const titre = 'CONTRAT CLIENT — ' + p.raisonSociale;

  const identsHtml = p.identifiants
    .map((i) => `${escHtml(i.label)} ${escHtml(i.value)}`)
    .join(' — ');

  const row = (label: string, html: string, fill = false) => `<tr>
    <td class="lbl">${escHtml(label)}</td>
    <td class="val${fill ? ' fill' : ''}">${html}</td>
  </tr>`;

  const fmtEUR = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  const articlesHtml = ARTICLES.map((a) => {
    const corpsHtml = a.corps
      .map((line) => `<p>${inline(line, p.raisonSociale, p.retourAdresse, p.retourEmail)}</p>`)
      .join('');
    return `<section class="article" data-pdf-section>
      <h3>Article ${a.num} — ${escHtml(a.titre)}</h3>
      ${corpsHtml}
    </section>`;
  }).join('');

  // ---- Article 4 : tableau des prestations
  const lignes = Array.isArray(data.proposition_lignes) ? data.proposition_lignes : [];
  const tvaPct = (() => {
    const ht = parseFloat(String(data.montant_ht ?? '').replace(/\s/g, '').replace(',', '.'));
    const tva = parseFloat(String(data.montant_tva ?? '').replace(/\s/g, '').replace(',', '.'));
    if (Number.isFinite(ht) && ht > 0 && Number.isFinite(tva)) {
      return `${(Math.round((tva / ht) * 1000) / 10).toLocaleString('fr-FR')}`;
    }
    return '—';
  })();

  const lignesHtml = lignes.length
    ? lignes.map((l) => {
        const pu = Number(l.prix_unitaire_ht) || 0;
        const qte = Number(l.quantite) || 0;
        const optsHtml = (l.options && l.options.length)
          ? `<div class="opts">${l.options.map((o) => `<div>${escHtml(/^comprenant\b/i.test(o.label) ? o.label : `Comprenant ${/^option\b/i.test(o.label) ? o.label : `Option ${o.label}`}`)}</div>`).join('')}</div>`
          : '';
        const descHtml = !optsHtml && l.description ? `<div class="opts">${escHtml(l.description)}</div>` : '';
        return `<tr>
          <td><strong>${escHtml(l.titre || 'Prestation')}</strong>${descHtml}${optsHtml}</td>
          <td class="c">${qte}</td>
          <td class="r">${escHtml(fmtEUR(pu))}</td>
          <td class="c">${tvaPct} %</td>
          <td class="r"><strong>${escHtml(fmtEUR(qte * pu * (1 + (parseFloat(tvaPct.replace(',', '.')) || 0) / 100)))}</strong></td>
        </tr>`;
      }).join('')
    : `<tr><td colspan="5" class="c" style="color:#7a8794">Voir devis ${v(data.devis_numero, '')}</td></tr>`;

  // ---- Article 4.2 : échéancier
  const echeances = Array.isArray(data.echeancier_paiement) ? data.echeancier_paiement : [];
  const fmtDate = (value?: string) => {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleDateString('fr-FR');
  };
  const fmtAmount = (value: number | string | null | undefined) => {
    const n = typeof value === 'number' ? value : parseFloat(String(value ?? '').replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? fmtEUR(n) : '—';
  };
  const echeancierHtml = echeances.length
    ? echeances.map((r) => `<tr><td>${escHtml(r.label || 'Échéance')}</td><td class="c">${escHtml(r.date ? fmtDate(r.date) : (/solde/i.test(r.label || '') ? (data.date_debut ? `Le jour de l’événement (${fmtDate(data.date_debut)})` : 'Le jour de l’événement') : '—'))}</td><td class="r"><strong>${escHtml(fmtAmount(r.montant))}</strong></td></tr>`).join('')
    : `<tr><td>Acompte / première échéance</td><td class="c">—</td><td class="r">—</td></tr>
       <tr><td>Solde</td><td class="c">Le jour de l’événement</td><td class="r">—</td></tr>`;

  const pageFooter = `${escHtml(p.raisonSociale)}&nbsp;&nbsp;|&nbsp;&nbsp;Contrat client&nbsp;&nbsp;|&nbsp;&nbsp;${escHtml(data.devis_numero || '')}`;

  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${escHtml(titre)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Source+Sans+3:wght@300;400;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4; margin: 16mm 14mm; }
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Source Sans 3',Arial,sans-serif;color:#1c2937;background:#fdfbf5;font-size:11px;line-height:1.55}
  .doc{max-width:780px;margin:0 auto;background:#fff;padding:30px 34px 36px}
  h1,h2,h3{font-family:'Libre Baskerville',Georgia,serif;font-weight:400;color:#0e2a3a}
  .cover{text-align:center;padding-bottom:16px;border-bottom:2px solid #c8a96a;margin-bottom:20px}
  .cover img{display:block;margin:0 auto 14px;width:150px;height:auto;object-fit:contain}
  .cover h1{font-size:34px;letter-spacing:2px;color:#c8a96a;text-transform:uppercase;font-weight:700}
  .cover .baseline{font-size:12px;font-style:italic;font-weight:600;color:#0e2a3a;margin-top:10px}
  .cover .legal{font-size:9.5px;color:#7a8794;margin-top:8px;line-height:1.55}
  h2{font-size:15px;color:#0e2a3a;margin:20px 0 8px;border-bottom:1px solid #ece4cf;padding-bottom:4px}
  h3.art{font-family:'Source Sans 3',sans-serif;font-size:12px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:#c8a96a;margin:18px 0 8px;border-bottom:1px solid #ece4cf;padding-bottom:4px}
  h4{font-family:'Source Sans 3',sans-serif;font-size:10.5px;font-weight:700;color:#c8a96a;margin:12px 0 5px}
  table.cp{width:100%;border-collapse:collapse;margin:6px 0 12px;font-size:11px}
  table.cp td{padding:6px 10px;border:1px solid #e6e0d2;vertical-align:top}
  table.cp td.lbl{background:#f2efe8;font-weight:700;color:#0e2a3a;width:30%;text-transform:uppercase;font-size:9.5px;letter-spacing:.6px}
  table.cp td.val{color:#2a3645}
  table.cp td.val.fill{background:#fdf6dd;color:#0e2a3a;font-weight:600}
  table.grid{width:100%;border-collapse:collapse;margin:6px 0 12px;font-size:11px}
  table.grid th{background:#0e2a3a;color:#fff;text-align:left;padding:7px 9px;font-size:9px;text-transform:uppercase;letter-spacing:1px}
  table.grid td{padding:6px 9px;border:1px solid #e6e0d2;color:#2a3645;background:#fdf6dd}
  table.grid td.c{text-align:center}
  table.grid td.r{text-align:right}
  table.grid th.c{text-align:center}table.grid th.r{text-align:right}
  .opts{margin-top:3px;padding-left:8px;border-left:2px solid #ece4cf;font-size:10px;color:#5a6a7a}
  .checklist{margin:4px 0 10px;font-size:11px;color:#2a3645}
  .checklist div{padding:2px 0}
  .cb{display:inline-block;font-size:13px;line-height:1;margin-right:5px;color:#c8a96a}
  .note{background:#0e2a3a;color:#fff;padding:9px 12px;font-size:9.5px;line-height:1.5;margin:10px 0 14px;border-radius:2px}
  .note strong{color:#c8a96a}
  .bullets{margin:8px 0 12px;padding-left:16px;font-size:10.5px;color:#2a3645}
  .bullets li{margin-bottom:4px;font-style:italic}
  .para{font-size:10.5px;color:#2a3645;text-align:justify;margin:6px 0}
  .article{margin-bottom:10px;page-break-inside:avoid}
  .article h3{font-size:12px;color:#0e2a3a;margin-bottom:4px;border-left:2px solid #c8a96a;padding-left:8px}
  .article p{margin-bottom:4px;text-align:justify;font-size:10.5px;color:#2a3645}
  .article p strong{color:#0e2a3a}
  .cge-intro{background:#faf6ec;border-left:3px solid #c8a96a;padding:10px 14px;margin:0 0 12px;font-size:10.5px;color:#3a4a5a;text-align:justify}
  .signature{display:grid;grid-template-columns:1fr 1fr;gap:0;margin-top:10px;page-break-inside:avoid;border:1px solid #e6e0d2}
  .signature .head{background:#f2efe8;font-size:9px;text-transform:uppercase;letter-spacing:1.2px;color:#0e2a3a;font-weight:700;padding:8px 12px;border-bottom:1px solid #e6e0d2}
  .signature .box{padding:12px 14px;min-height:180px;font-size:10.5px;color:#2a3645}
  .signature .box.client{background:#fdf6dd;border-left:1px solid #e6e0d2}
  .signature .head.client{border-left:1px solid #e6e0d2}
  .signature .box .name{font-weight:700;color:#0e2a3a}
  .signature .box img.tampon{display:block;margin:6px 0 0;width:210px;max-width:100%;height:auto;mix-blend-mode:multiply}
  img.rib{display:block;width:100%;max-width:660px;height:auto;border:1px solid #e6e0d2;margin:6px 0 4px}

  .signature .box .line{margin-top:5px}
  .footer{border-top:1px solid #ece4cf;padding-top:10px;margin-top:20px;font-size:9px;color:#7a8794;text-align:center;line-height:1.6}
  @media print{body{background:#fff}.doc{padding:0;max-width:none}}
</style></head><body>
<div class="doc">

  <div class="cover" data-pdf-section>
    <img src="${logoSrc(structure)}" alt="${escHtml(p.raisonSociale)}"/>
    <h1>Contrat client</h1>
    <div class="baseline">Prestations de services | Décoration | Location de matériel | Prestations artistiques</div>
    <div class="legal">CYGNATURE AGENCY - identité commerciale exploitée par ${escHtml(p.raisonSociale)} ${escHtml(p.forme)} | ${escHtml(p.adresse)} | ${identsHtml} | RCS Troyes B 898 473 962 | ${escHtml(p.email)} | 06 71 35 15 50</div>
  </div>

  <div data-pdf-section><h2>Conditions Particulières (CP)</h2></div>

  <!-- ARTICLE 1 -->
  <div data-pdf-section>
    <h3 class="art">Article 1 — Identification des parties et commande</h3>
    <h4>1.1 Le prestataire</h4>
    <table class="cp">
      ${row('Dénomination', `${escHtml(p.raisonSociale)} — ${escHtml(p.forme)}`)}
      ${row('Adresse', escHtml(p.adresse))}
      ${row('Représentant', `${escHtml(p.representant)}, agissant en qualité de représentant légal`)}
      ${row('Immatriculation', identsHtml)}
      ${row('Contact', `${escHtml(p.email)} — Tél. : 06 71 35 15 50`)}
    </table>
  </div>

  <div data-pdf-section>
    <h4>1.2 Le client</h4>
    <table class="cp">
      ${row('Nom / raison sociale', v(data.client_nom), true)}
      ${row('Représenté par', v(data.client_representant), true)}
      ${row('Adresse complète', `${v(data.client_adresse, '')} ${v(data.client_cp_ville, '')}`.trim() || '—', true)}
      ${row('Téléphone', v((data as any).client_tel), true)}
      ${row('E-mail', v((data as any).client_email), true)}
      ${row('SIRET / TVA', v((data as any).client_siret, 'Client particulier — non applicable'), true)}
    </table>
  </div>

  <div data-pdf-section>
    <h4>1.3 Références contractuelles</h4>
    <table class="cp">
      ${row('Référence', v(data.devis_numero), true)}
      ${row('Date d’émission', v(data.devis_date), true)}
      ${row('Durée du contrat', `${v(data.date_debut, '—')} à ${v(data.date_fin, '—')}`, true)}
      ${row('Durée de validité', VALIDITE_RESERVATION.map((t) => `<p class="para" style="font-style:italic">${escHtml(t)}</p>`).join(''))}
      ${row('Documents applicables', 'Les présentes CP, le devis/bon de commande, les CGV CYGNATURE')}
    </table>
    <ul class="bullets">
      <li>En cas de contradiction, les présentes Conditions Particulières prévalent pour les seuls éléments expressément négociés et renseignés dans le présent document.</li>
      <li>Dès acceptation / signature et jusqu'à l'exécution complète des prestations et obligations prévues, notamment paiement et restitution du matériel s'il y a lieu.</li>
    </ul>
  </div>

  <!-- ARTICLE 2 -->
  <div data-pdf-section>
    <h3 class="art">Article 2 — Événement et lieu d’exécution</h3>
    <table class="cp">
      ${row('Nature de l’événement', v(data.type_event), true)}
      ${row('Intitulé', v(data.event_intitule), true)}
      ${row('Date', v(data.date_debut), true)}
      ${row('Intervention CYGNATURE', `Début : ${v(data.heure_debut, '—')} — Fin : ${v(data.heure_fin, '—')}`, true)}
      ${row('Lieu', v(data.lieu), true)}
      ${row('Adresse du lieu', v(data.lieu), true)}
      ${row('Contact sur place', v((data as any).contact_sur_place, `${v(data.client_representant || data.client_nom, 'Le Client')} — ${v((data as any).client_tel, 'coordonnées communiquées avant l’événement')}`), true)}
    </table>
  </div>

  <!-- ARTICLE 3 -->
  <div data-pdf-section>
    <h3 class="art">Article 3 — Périmètre de la prestation</h3>
    <div style="font-weight:700;color:#0e2a3a;font-size:11px">Nature retenue :</div>
    <div class="checklist">
      <div>${cb(data.cb_services)} Services / coordination / organisation</div>
      <div>${cb(data.cb_artistique)} Prestation artistique / production / diffusion</div>
      <div>${cb((data as any).cb_decoration)} Décoration / scénographie</div>
      <div>${cb(data.cb_location)} Location de matériel</div>
      <div>${cb((data as any).cb_photobooth)} Photobooth / animation photo</div>
      <div>${cb((data as any).cb_technique)} Sonorisation / éclairage / technique</div>
      <div>${cb(!!(data as any).autre_nature)} Autre : ${v((data as any).autre_nature, '—')}</div>
    </div>
    <h4>3.1 Description contractuelle</h4>
    <table class="cp">
      ${row('Prestation retenue', 'En référence au devis', true)}
      ${row('Description', 'En référence au devis', true)}
      ${row('Inclus', v(data.materiel), true)}
      ${row('Exclus', v((data as any).exclus), true)}
      ${row('Livrables', v((data as any).livrables), true)}
    </table>
    <div class="note"><strong>PRÉCISION CONTRACTUELLE</strong> — Toute demande supplémentaire ou modification substantielle après validation pourra faire l’objet d’un avenant et d’une facturation complémentaire après accord du client.</div>
  </div>

  <!-- ARTICLE 3 BIS — WEDDING PLANNER (toujours inclus, sans bloc de validation) -->
  ${`<div data-pdf-section>
    <h3 class="art">Article 3 BIS — Wedding Planner : périmètre de mission, organisation &amp; coordination</h3>
    <p class="para">${inline(ARTICLE_3BIS_INTRO, p.raisonSociale, p.retourAdresse, p.retourEmail)}</p>
    ${ARTICLE_3BIS.map((s) => `
      <h4>${escHtml(s.num)} — ${escHtml(s.titre)}</h4>
      ${(s.paras || []).map((t) => `<p class="para">${inline(t, p.raisonSociale, p.retourAdresse, p.retourEmail)}</p>`).join('')}
      ${s.bulletsIntro ? `<p class="para">${inline(s.bulletsIntro, p.raisonSociale, p.retourAdresse, p.retourEmail)}</p>` : ''}
      ${(s.bullets && s.bullets.length) ? `<ul class="bullets">${s.bullets.map((b) => `<li>${inline(b, p.raisonSociale, p.retourAdresse, p.retourEmail)}</li>`).join('')}</ul>` : ''}
    `).join('')}
  </div>`}

  <!-- ARTICLE 4 -->
  <div data-pdf-section>
    <h3 class="art">Article 4 — Prix, réservation et règlements</h3>
    <table class="grid">
      <thead><tr><th>Prestation</th><th class="c">Qté</th><th class="r">P.U. HT</th><th class="c">TVA</th><th class="r">Total TTC</th></tr></thead>
      <tbody>${lignesHtml}</tbody>
    </table>
    <table class="cp">
      ${row('Montant HT', `<strong>${v(data.montant_ht)} €</strong>`, true)}
      ${row('TVA', `${tvaPct} % — ${v(data.montant_tva)} €`, true)}
      ${row('Montant TTC', `<strong>${v(data.montant_ttc)} €</strong> — ${String(v(data.montant_ttc_lettres, '')).replace(/\s*(euros?\s*TTC)?\s*$/i, '')} euros TTC`, true)}
      ${((data as any).cb_cheque_garantie || (data as any).cheque_garantie_montant) ? row('Chèque de garantie', `<p class="para">Chèque de garantie correspondant au montant total du devis et du contrat, sera demandé à titre de garantie.</p>`) : ''}
    </table>

    <h4>4.1 Modalités de réservation</h4>
    <p class="para">La date et la prestation sont définitivement réservées après réception cumulative du document signé et du premier règlement prévu ci-dessous, sous réserve de disponibilité au moment de l’accord.</p>
    <div class="checklist" style="white-space:pre-line">${escHtml((data.modalites_paiement && data.modalites_paiement.trim()) ? data.modalites_paiement : 'Mode de paiement : À définir')}</div>

    <h4>4.2 Échéancier</h4>
    <table class="grid">
      <thead><tr><th>Échéance</th><th class="c">Date limite</th><th class="r">Montant TTC</th></tr></thead>
      <tbody>${echeancierHtml}</tbody>
    </table>
    <p class="para">Tout retard ou défaut de paiement entraîne l’application des dispositions prévues aux CGV et peut suspendre la préparation ou l’exécution de la prestation. Pour un client professionnel, préciser dans les documents applicables le taux des pénalités et l’indemnité forfaitaire de recouvrement en vigueur.</p>
  </div>

  <!-- ARTICLE 5 -->
  <div data-pdf-section>
    <h3 class="art">Article 5 — Conditions techniques, accès et sécurité</h3>
    <table class="cp">
      ${row('Accès', v((data as any).acces), true)}
      ${row('Stationnement', v((data as any).stationnement), true)}
      ${row('Électricité', v((data as any).electricite), true)}
      ${row('Stockage', v((data as any).stockage), true)}
      ${row('Accessibilité', v(data.contraintes), true)}
    </table>
  </div>

  <!-- ARTICLE 6 -->
  <div data-pdf-section>
    <h3 class="art">Article 6 — Accueil des intervenants et frais</h3>
    <table class="cp">
      ${row('Repas / hébergement', `${cb(!!data.collations?.trim())} Collation/boissons &nbsp; ${cb(!!data.repas?.trim())} Repas/boissons &nbsp; ${cb(!!data.hebergements?.trim())} Hébergement`
        + (data.collations?.trim() ? `<br>Collations : ${escHtml(data.collations)}` : '')
        + (data.repas?.trim() ? `<br>Repas : ${escHtml(data.repas)}` : '')
        + (data.hebergements?.trim() ? `<br>Hébergement : ${escHtml(data.hebergements)}` : ''), true)}
      ${row('Loge / espace privé', v((data as any).loge_espace, '1 table / 2 chaises / Portant à vêtements'), true)}
    </table>
  </div>

  <!-- ARTICLE 7 -->
  <div data-pdf-section>
    <h3 class="art">Article 7 — Rôle contractuel de CYGNATURE</h3>
    <div class="checklist">
      <div>${cb((data as any).role_producteur)} Producteur / employeur des artistes</div>
      <div>${cb((data as any).role_diffuseur)} Diffuseur / organisateur artistique</div>
      <div>${cb((data as any).role_prestataire ?? (data.cb_services || data.cb_artistique))} Prestataire direct</div>
      <div>${cb((data as any).role_intermediaire)} Intermédiaire / coordinateur — les contrats et paiements concernés sont précisés en annexe</div>
      <div>${cb((data as any).role_loueur ?? data.cb_location)} Loueur de matériel</div>
    </div>
    <p class="para"><strong>Rôle retenu et répartition des responsabilités :</strong> ${v((data as any).role_detail, `CYGNATURE PRODUCTION intervient en qualité de prestataire direct et assure la coordination des intervenants${data.cb_location ? ', ainsi que la mise à disposition du matériel loué' : ''}. Le Client assure l'accueil sur site, l'accès au lieu et le respect des conditions d'installation.`)}</p>
  </div>

  <!-- ARTICLE 8 -->
  ${data.cb_location || data.materiel ? `<div data-pdf-section>
    <h3 class="art">Article 8 — Location de matériel | module conditionnel</h3>
    <table class="cp">
      ${row('Matériel loué', v(data.materiel, `Voir Réf. Devis ${escHtml(data.devis_numero || '')}`), true)}
      ${row('Remise', v((data as any).remise_texte, 'A DEFINIR'), true)}
      ${row('Retour', v((data as any).retour_texte, 'A DEFINIR'), true)}
      ${row('Dépôt de garantie', v((data as any).depot_garantie_texte, data.caution ? `${escHtml(String(data.caution))} € pour Location Lumières et Rideaux/Fonds de scène` : '1 000 € pour Location Lumières et Rideaux/Fonds de scène'), true)}
      ${row('Pièce d’identité', `${cb((data as any).cb_pi_verifiee)} Vérifiée &nbsp; ${cb((data as any).cb_pi_copie)} Copie autorisée et conservée selon la politique applicable<br/>${cb((data as any).cb_pi_non)} Non requise`)}
      ${row('Nettoyage', `${cb((data as any).cb_nettoyage_inclus)} Inclus &nbsp; ${cb(data.cb_nettoyage_non)} Client &nbsp; ${cb(data.cb_nettoyage_oui)} Option ${(data as any).nettoyage_montant ? escHtml(String((data as any).nettoyage_montant)) : '______'} €`, true)}
    </table>
  </div>` : ''}

  <!-- ARTICLE 9 -->
  ${(data as any).cb_photobooth || /photobooth/i.test(String(data.materiel || '')) ? `<div data-pdf-section>
    <h3 class="art">Article 9 — Photobooth | module conditionnel</h3>
    <table class="cp">
      ${row('Formule', escHtml(String((data as any).photobooth_formule || '')), true)}
      ${row('Personnalisation', escHtml(String((data as any).photobooth_personnalisation || '')), true)}
      ${row('Option IA', v((data as any).photobooth_ia, 'RAS'), true)}
      ${row('Installation', escHtml(String((data as any).photobooth_installation || '')), true)}
    </table>
    <table class="cp">
      ${row('Dépassements', escHtml(DEPASSEMENTS_TEXTE))}
    </table>
  </div>` : ''}

  <div data-pdf-section>
    <table class="cp">
      ${row("Droit à l'image client", DROIT_IMAGE_CLIENT.map((t) => `<p class="para">${escHtml(t)}</p>`).join(''))}
      ${row('Conditions spéciales', v(data.particularites || data.autorisations || data.contraintes, 'Néant'), true)}
    </table>
  </div>

  <!-- CONDITIONS GÉNÉRALES -->
  <div data-pdf-section>
    <h2>Conditions générales essentielles</h2>
    <div class="cge-intro">${inline(PREAMBULE_CGE, p.raisonSociale, p.retourAdresse, p.retourEmail)}</div>
  </div>
  ${articlesHtml}

  <!-- ANNEXE RIB -->
  <div data-pdf-section>
    <h2>Annexe — Coordonnées bancaires (RIB)</h2>
    ${structure === 'cygnature'
      ? `<img class="rib" src="${RIB_IMAGE_DATA_URI}" alt="Relevé d'identité bancaire ${escHtml(RIB_CYGNATURE.titulaire)}"/>`
      : `<table class="cp">
      ${row('Titulaire', escHtml(RIB_CYGNATURE.titulaire))}
      ${row('Banque / domiciliation', `${escHtml(RIB_CYGNATURE.banque)} — ${escHtml(RIB_CYGNATURE.domiciliation)}`)}
      ${row('IBAN', `<strong>${escHtml(RIB_CYGNATURE.iban)}</strong>`)}
      ${row('BIC', `<strong>${escHtml(RIB_CYGNATURE.bic)}</strong>`)}
    </table>`}
  </div>

  <!-- SIGNATURES -->
  <div data-pdf-section>
    <h2>Signatures</h2>
    <p class="para">Le Client reconnaît avoir reçu et accepté avant son engagement les CP, le devis et les présentes conditions, et avoir pu demander toute précision utile.</p>
    <p class="para"><strong>Référence devis${String((data as any).type_domaine || '').trim() ? ` — Prestation « ${escHtml(String((data as any).type_domaine).trim())} »` : ''} — N° ${v(data.devis_numero, '—')}</strong></p>
    <div class="signature">
      <div class="head">Pour ${escHtml(p.raisonSociale)}</div>
      <div class="head client">Pour le client</div>
      <div class="box">
        <div class="name">${escHtml(p.representant)} - Président</div>
        <div class="line">Fait à : <strong>SAINT-POUANGE</strong></div>
        <div class="line">Le : ${v(data.date_signature, '____ / ____ / ______')}</div>
        <div class="line">Signature :</div>
        ${structure === 'cygnature' ? `<img class="tampon" src="${tamponSrc(structure)}" alt="Tampon et signature ${escHtml(p.representant)}"/>` : ''}

      </div>
      <div class="box client">
        <div class="line">Nom / qualité :</div>
        <div class="name">${escHtml(data.nom_signataire_client || data.client_nom || '—')}</div>
        <div class="line">Fait à :</div>
        <div class="line">Le :</div>
        <div class="line" style="margin-top:10px">Signature précédée de « Lu et approuvé » :</div>
      </div>
    </div>
  </div>

  <div class="footer" data-pdf-section>
    ${pageFooter}<br>${escHtml(p.footer)}
  </div>

</div>
</body></html>`;
}
